import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { usePageTracking } from "../hooks/usePageTracking";
import { useAuth } from "../contexts/AuthContext";
import {
  useActivityActions,
  useActivityFeedData,
  useActivityRecommendationsData,
} from "../contexts/ActivityContext";
import {
  SparklesIcon,
  MapPinIcon,
  ClockIcon,
} from "../components/icons/FeatherIcons";
import {
  type ActivityRequest,
  type VibeCheckUser,
  type MatchProposal,
} from "../types";
import { AnimatePresence, motion } from "framer-motion";
import {
  SuspenseSpotlightCard,
  SuspenseVibeCard,
  SuspenseVibeStream,
  SuspenseActivityModal,
} from "../components/LazyComponents";
import { useToast } from "../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import { toJsDate } from "../utils/toJsDate";
import { fetchForYouMatchProposal } from "../services/matchSuggestionService";
import {
  getDocs,
  collection,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { crashlytics } from "../services/firebase";

const Skeleton: React.FC = () => (
  <div className="w-full flex-shrink-0 animate-pulse break-inside-avoid mb-4">
    <div className="bg-gray-200 h-36 rounded-2xl"></div>
    <div className="h-4 bg-gray-200 rounded mt-2 w-3/4"></div>
  </div>
);

const ForYouPage: React.FC = () => {
  usePageTracking("ForYou", "ForYouPage");
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { activities, passedActivityIds } = useActivityFeedData();
  const {
    recommendations,
    vibeCheckUsers,
    isLoadingRecommendations,
  } = useActivityRecommendationsData();
  const {
    fetchRecommendations,
  } = useActivityActions();

  const [selectedActivity, setSelectedActivity] =
    useState<ActivityRequest | null>(null);
  const lastFetchTimeRef = useRef<number>(0);
  const fetchingRecommendationsRef = useRef<boolean>(false);
  const hasAttemptedInitialFetchRef = useRef<boolean>(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreRecommendations, setHasMoreRecommendations] = useState(true);
  const [matchProposal, setMatchProposal] = useState<MatchProposal | null>(
    null,
  );
  const [isLoadingMatchProposal, setIsLoadingMatchProposal] = useState(false);
  const [candidateUsers, setCandidateUsers] = useState<any[]>([]);
  const matchProposalFetchRef = useRef(false);

  // Memoize active activities to prevent unnecessary re-computations
  const activeActivities = useMemo(
    () => activities.filter((a) => !a.isDeleted),
    [activities],
  );

  // Debounced fetch function to prevent too frequent calls
  const debouncedFetchRecommendations = useCallback(
    (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;

    if (fetchingRecommendationsRef.current || isLoadingRecommendations) {
      if (!silent) {
        addToast(`Fetching recommendations is in progress. Please wait.`, "info");
      }
      return;
    }

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;
    const cooldownTime = 30000; // 30 seconds cooldown

    if (timeSinceLastFetch < cooldownTime) {
      // calculate how much time is left
      const timeLeft = Math.ceil((cooldownTime - timeSinceLastFetch) / 1000);
      if (!silent) {
        addToast(
          `Please wait for ${timeLeft} seconds before refreshing again.`,
          "info",
        );
      }
      return;
    }

    fetchingRecommendationsRef.current = true;
    fetchRecommendations()
      .then((didFetch) => {
        if (didFetch) {
          if (!silent) {
            addToast("Recommendations updated!", "success");
          }
        } else {
          if (!silent) {
            addToast(
              "No new recommendations. Create or join activities to get started.",
              "info",
            );
          }
        }
      })
      .catch(() => {
        if (!silent) {
          addToast(
            "Failed to fetch recommendations. Please try again later.",
            "error",
          );
        }
      })
      .finally(() => {
        fetchingRecommendationsRef.current = false;
        // Apply cooldown even when fetch returns no data or fails to avoid retry loops.
        lastFetchTimeRef.current = Date.now();
      });
    },
    [fetchRecommendations, isLoadingRecommendations, addToast],
  );

  useEffect(() => {
    hasAttemptedInitialFetchRef.current = false;
  }, [currentUser?.id]);

  // Reset pagination when new recommendations arrive
  useEffect(() => {
    if (vibeCheckUsers.length > 0) {
      setCurrentPage(0);
    }
  }, [vibeCheckUsers.length]);

  // Optimized effect with better dependency management
  useEffect(() => {
    // Only fetch if we truly have no data and user is ready
    if (
      currentUser &&
      recommendations.length === 0 &&
      vibeCheckUsers.length === 0 &&
      !isLoadingRecommendations &&
      !hasAttemptedInitialFetchRef.current
    ) {
      // Add a small delay to allow initial render before heavy operation
      const timeoutId = setTimeout(() => {
        hasAttemptedInitialFetchRef.current = true;
        debouncedFetchRecommendations({ silent: true });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    currentUser,
    recommendations.length,
    vibeCheckUsers.length,
    isLoadingRecommendations,
    debouncedFetchRecommendations,
  ]);

  useEffect(() => {
    if (!currentUser || matchProposalFetchRef.current) return;

    matchProposalFetchRef.current = true;
    setIsLoadingMatchProposal(true);

    fetchForYouMatchProposal()
      .then((proposal) => {
        if (proposal) {
          setMatchProposal(proposal);
        }
      })
      .finally(() => {
        setIsLoadingMatchProposal(false);
      });
  }, [currentUser]);

  // Fetch candidate users when match proposal loads
  useEffect(() => {
    if (
      !matchProposal?.candidateUserIds ||
      matchProposal.candidateUserIds.length === 0
    ) {
      setCandidateUsers([]);
      return;
    }

    const fetchCandidates = async () => {
      const firestore = db;
      if (!firestore) return;

      try {
        const userPromises = matchProposal.candidateUserIds.map(
          async (userId) => {
            const userDocRef = doc(firestore, "users", userId);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              return {
                id: userDoc.id,
                ...userDoc.data(),
              };
            }
            return null;
          },
        );
        const users = (await Promise.all(userPromises)).filter(Boolean);
        setCandidateUsers(users);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : JSON.stringify(error);

        crashlytics.logError(
          `Failed to fetch candidate users: ${errorMessage}`,
          "ForYouPage.tsx",
          error as any,
        );
      }
    };

    fetchCandidates();
  }, [matchProposal?.candidateUserIds]);

  // Optimized user interests with better caching
  const userInterests = useMemo(() => {
    if (!currentUser) return [];

    const userActivities = activeActivities.filter((a) =>
      a.participants.some((p) => p.id === currentUser.id),
    );

    if (userActivities.length === 0) {
      // For new users, use their stated passions from profile builder if available
      if (currentUser.passions && currentUser.passions.length > 0) {
        return currentUser.passions.slice(0, 3);
      }
      // Fallback to default interests if no passions specified
      return ["dining", "outdoor", "movies"];
    }

    const categoryCounts = userActivities.reduce(
      (acc, activity) => {
        acc[activity.category] = (acc[activity.category] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const sortedInterests = Object.entries(categoryCounts)
      .sort(([, countA], [, countB]) => (countB as number) - (countA as number))
      .map(([category]) => category);

    // Ensure we always have at least 3 interests for visual consistency
    while (sortedInterests.length < 3) {
      // Use passions as defaults if available, otherwise use hardcoded defaults
      const defaults =
        currentUser.passions && currentUser.passions.length > 0
          ? [
              ...currentUser.passions,
              "dining",
              "outdoor",
              "movies",
              "events",
            ].filter((d) => !sortedInterests.includes(d))
          : ["dining", "outdoor", "movies", "events"].filter(
              (d) => !sortedInterests.includes(d),
            );
      if (defaults[0]) sortedInterests.push(defaults[0]);
    }

    return sortedInterests.slice(0, 3);
  }, [activeActivities, currentUser?.id, currentUser?.passions]);

  // Track current slide index for activity recommendations slider
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Swipe handling
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentSlideIndex < recommendations.length - 1) {
      setCurrentSlideIndex((prev) => prev + 1);
    }
    if (isRightSwipe && currentSlideIndex > 0) {
      setCurrentSlideIndex((prev) => prev - 1);
    }
  };

  // Reset slide index when recommendations change
  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [recommendations.length]);

  // Implement pagination for feed items - show items in batches
  const ITEMS_PER_PAGE = 6;
  const paginatedFeedItems = useMemo(() => {
    const startIndex = 0;
    const endIndex = (currentPage + 1) * ITEMS_PER_PAGE;
    return vibeCheckUsers.slice(startIndex, endIndex);
  }, [vibeCheckUsers, currentPage]);

  useEffect(() => {
    const endIndex = (currentPage + 1) * ITEMS_PER_PAGE;
    setHasMoreRecommendations(endIndex < vibeCheckUsers.length);
  }, [currentPage, vibeCheckUsers.length]);

  const loadMoreRecommendations = useCallback(() => {
    if (hasMoreRecommendations && !isLoadingRecommendations) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMoreRecommendations, isLoadingRecommendations]);

  const formatProposalDate = (dateString?: string) => {
    if (!dateString) return "TBD";
    const date = toJsDate(dateString);
    if (!date || isNaN(date.getTime())) return "TBD";
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Early return with loading if user not ready
  if (!currentUser) {
    return (
      <div className="bg-gray-50 h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Memoized loading skeleton for better performance
  const LoadingSkeleton = () => {
    const [index, setIndex] = useState(0);
    const [tipIndex, setTipIndex] = useState(0);

    const tips = [
      {
        icon: "💡",
        title: "Pro Tip",
        text: "Users with a profile photo get 3x more relevant matches.",
      },
      {
        icon: "🤝",
        title: "Community",
        text: "Over 500 people in Singapore are looking for activities right now.",
      },
      {
        icon: "✨",
        title: "Did you know?",
        text: "Adding 'Interests' helps our AI find groups that actually fit your vibe.",
      },
      {
        icon: "🔒",
        title: "Privacy",
        text: "Your exact location is never shared without your permission.",
      },
    ];

    const loadingTips = [
      "Analyzing your unique interests...",
      "Matching you with like-minded creators...",
      "Gathering the best recommendations...",
      "Syncing with community trends...",
    ];

    // This effect handles the text rotation
    useEffect(() => {
      const interval = setInterval(() => {
        setIndex((prev) => (prev + 1) % loadingTips.length);
      }, 2500); // Changes every 2.5 seconds
      return () => clearInterval(interval);
    }, [loadingTips.length]);

    useEffect(() => {
      const interval = setInterval(() => {
        setTipIndex((prev) => (prev + 1) % tips.length);
      }, 2000); // Change tip every 2 seconds
      return () => clearInterval(interval);
    }, []);

    const skeletonContent = useMemo(
      () => (
        <div className="p-6 max-w-4xl mx-auto min-h-[500px] flex flex-col">
          {/* 1. Dynamic Status Header - Uses the index state */}
          <div className="text-center mb-10 h-24">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600 mb-4">
              <span className="relative flex h-2 w-2 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
              </span>
              <span className="text-xs font-bold uppercase tracking-widest">
                Processing
              </span>
            </div>

            {/* This is where the loadingTips are used */}
            <h3 className="text-2xl font-bold text-gray-800 transition-all duration-500 ease-in-out">
              {loadingTips[index]}
            </h3>
            <p className="text-gray-500 text-sm mt-2">
              Just a moment while we personalize your feed.
            </p>
          </div>

          {/* 2. Main Skeletons */}
          <div className="space-y-6">
            <div className="relative overflow-hidden bg-gray-100 h-48 rounded-3xl animate-shimmer">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_2s_infinite]"></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="h-32 bg-gray-100 rounded-2xl animate-pulse"></div>
              <div className="h-32 bg-gray-100 rounded-2xl animate-pulse"></div>
            </div>
          </div>

          {/* 3. Rotating Tips Box at the bottom */}
          <div className="mt-auto px-2 pb-4">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-[2rem] p-5 shadow-sm overflow-hidden relative">
              {/* Animated background element */}
              <div className="absolute -right-4 -top-4 w-20 h-20 bg-white/40 rounded-full blur-2xl" />

              <div className="flex items-start space-x-4 relative z-10">
                <div className="bg-white p-3 rounded-2xl shadow-sm text-xl">
                  {tips[tipIndex].icon}
                </div>

                <div className="flex-1 min-h-[60px]">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={tipIndex}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      <h4 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">
                        {tips[tipIndex].title}
                      </h4>
                      <p className="text-sm text-amber-900 leading-snug font-medium">
                        {tips[tipIndex].text}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* Progress Bar for the timer */}
              <div className="absolute bottom-0 left-0 h-1 bg-amber-200/50 w-full">
                <motion.div
                  key={tipIndex}
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 4, ease: "linear" }}
                  className="h-full bg-amber-400"
                />
              </div>
            </div>
          </div>
        </div>
      ),
      [index],
    ); // Re-memoize when index changes to update the text

    return skeletonContent;
  };

  const renderContent = useCallback(() => {
    if (isLoadingRecommendations) {
      return <LoadingSkeleton />;
    }

    if (recommendations.length === 0 && paginatedFeedItems.length === 0) {
      return (
        <div className="text-center py-20 px-4 flex flex-col items-center">
          <SparklesIcon className="w-16 h-16 text-primary/50 mb-4" />
          <p className="text-lg font-semibold text-gray-500">
            No recommendations yet!
          </p>
          <p className="text-gray-500 mt-1">
            Join a few activities to help our AI learn what you love.
          </p>
        </div>
      );
    }

    return (
      <div className="p-4">
        {/* Activity Recommendations Slider */}
        {recommendations.length > 0 && (
          <div className="relative mb-6">
            {/* Slider Container */}
            <div
              className="relative overflow-hidden"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div
                className="flex transition-transform duration-300 ease-out"
                style={{
                  transform: `translateX(-${currentSlideIndex * 100}%)`,
                }}
              >
                {recommendations.map((activity, index) => (
                  <div
                    key={`activity-slide-${activity.id}`}
                    className="w-full flex-shrink-0"
                  >
                    <SuspenseSpotlightCard
                      activity={activity}
                      onViewDetails={() => setSelectedActivity(activity)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            {recommendations.length > 1 && (
              <>
                {/* Previous Button */}
                {currentSlideIndex > 0 && (
                  <button
                    onClick={() => setCurrentSlideIndex((prev) => prev - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors z-10"
                    aria-label="Previous activity"
                  >
                    <svg
                      className="w-6 h-6 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                )}

                {/* Next Button */}
                {currentSlideIndex < recommendations.length - 1 && (
                  <button
                    onClick={() => setCurrentSlideIndex((prev) => prev + 1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors z-10"
                    aria-label="Next activity"
                  >
                    <svg
                      className="w-6 h-6 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                )}

                {/* Dots Indicator */}
                <div className="flex justify-center space-x-2 mt-4">
                  {recommendations.map((_, index) => (
                    <button
                      key={`dot-${index}`}
                      onClick={() => setCurrentSlideIndex(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === currentSlideIndex
                          ? "w-8 bg-primary"
                          : "w-2 bg-gray-300 hover:bg-gray-400"
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {isLoadingMatchProposal && !matchProposal && (
          <div className="mb-6 w-full animate-pulse rounded-2xl border border-amber-100 bg-amber-50/60 p-5">
            <div className="h-3 w-24 rounded bg-amber-200/70"></div>
            <div className="mt-3 h-5 w-3/4 rounded bg-amber-200/70"></div>
            <div className="mt-2 h-4 w-1/2 rounded bg-amber-200/60"></div>
            <div className="mt-4 h-9 w-32 rounded-lg bg-amber-200/70"></div>
          </div>
        )}

        {matchProposal?.activityDraft && (
          <div className="mb-6 rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 via-white to-amber-100/60 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                Match suggestion
              </div>
              <span className="text-xs text-amber-700">Just for you</span>
            </div>
            <h3 className="mt-2 text-lg font-bold text-gray-900">
              {matchProposal.activityDraft.title || "Suggested hangout"}
            </h3>
            {matchProposal.activityDraft.description && (
              <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                {matchProposal.activityDraft.description}
              </p>
            )}

            <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-gray-600">
              <div className="flex items-center gap-2">
                <MapPinIcon className="h-4 w-4 text-amber-600" />
                <span className="font-semibold text-gray-800">
                  {matchProposal.activityDraft.location || "Nearby"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="h-4 w-4 text-amber-600" />
                <span className="font-semibold text-gray-800">
                  {formatProposalDate(
                    matchProposal.activityDraft.suggestedDateTime,
                  )}
                </span>
              </div>
              <div className="text-gray-500">
                Group size:{" "}
                <span className="font-semibold text-gray-800">
                  {matchProposal.activityDraft.groupSize || 2}
                </span>
              </div>
              <div className="text-gray-500">
                Category:{" "}
                <span className="font-semibold text-gray-800">
                  {matchProposal.activityDraft.category || "general"}
                </span>
              </div>
            </div>

            {candidateUsers.length > 0 && (
              <div className="mt-4 pt-4 border-t border-amber-100">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-3">
                  Who's invited
                </p>
                <div className="flex items-center gap-3">
                  {candidateUsers.map((user) => (
                    <div key={user.id} className="text-center">
                      <div className="h-12 w-12 rounded-full bg-gray-200 overflow-hidden border-2 border-amber-200 mx-auto mb-1">
                        {user.profileImageUrl ? (
                          <img
                            src={user.profileImageUrl}
                            alt={user.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-amber-200 to-amber-300 flex items-center justify-center text-xs font-bold text-amber-700">
                            {user.username?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <p className="text-xs font-semibold text-gray-700 truncate max-w-[50px]">
                        {user.username}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {matchProposal.candidateUserIds?.length || 0} match
                {matchProposal.candidateUserIds?.length === 1 ? "" : "es"}{" "}
                included
              </div>
              <button
                onClick={() =>
                  navigate(`/create?proposalId=${matchProposal.id}`)
                }
                className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-600"
              >
                Review suggestion
              </button>
            </div>
          </div>
        )}

        {paginatedFeedItems.length > 0 && userInterests.length > 0 && (
          <SuspenseVibeStream
            interests={userInterests}
            guidingText="I've noticed you're into things like..."
          />
        )}

        {paginatedFeedItems.length > 0 && (
          <>
            <div className="space-y-4 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
              {paginatedFeedItems.map((item) => (
                <SuspenseVibeCard
                  key={`vibe-${item.user.id}`}
                  vibeCheckUser={item}
                />
              ))}
            </div>

            {hasMoreRecommendations && (
              <div className="mt-6 text-center">
                <button
                  onClick={loadMoreRecommendations}
                  disabled={isLoadingRecommendations}
                  className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isLoadingRecommendations ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    "Show More People"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }, [
    isLoadingRecommendations,
    LoadingSkeleton,
    recommendations,
    currentSlideIndex,
    paginatedFeedItems,
    userInterests,
    hasMoreRecommendations,
    loadMoreRecommendations,
    isLoadingMatchProposal,
    matchProposal,
    navigate,
    formatProposalDate,
  ]);

  return (
    <div className="bg-white h-full overflow-y-auto">
      <SuspenseActivityModal
        isOpen={!!selectedActivity}
        onClose={() => setSelectedActivity(null)}
        activity={selectedActivity}
      />

      <header className="bg-white/80 backdrop-blur-lg px-4 pt-4 pb-4 border-b border-gray-200/80 sticky-header">
        <div className="relative flex items-center justify-center">
          <h1 className="text-xl font-bold text-gray-900 flex items-center justify-center gap-2">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                textShadow: ["0 0 4px #fff", "0 0 8px #C4B5FD", "0 0 4px #fff"],
                filter: ["saturate(1)", "saturate(1.5)", "saturate(1)"],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <SparklesIcon
                className="w-6 h-6 text-primary"
                fill="currentColor"
              />
            </motion.div>
            <span>For You</span>
          </h1>
          <button
            onClick={() => {
              setCurrentPage(0);
              debouncedFetchRecommendations();
            }}
            disabled={isLoadingRecommendations}
            className="absolute right-0 text-primary hover:text-primary/80 disabled:opacity-50 p-1 flex items-center gap-1"
            title="Refresh recommendations"
            aria-label="Refresh recommendations"
          >
            <svg
              className={`w-5 h-5 ${isLoadingRecommendations ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582M20 20v-5h-.581M5.635 19.364A9 9 0 104.582 9.582"
              />
            </svg>
            <span className="sr-only">Refresh</span>
          </button>
        </div>
      </header>

      <main>{renderContent()}</main>
    </div>
  );
};

export default ForYouPage;
